// Value Indicator — top model-vs-market edges across five markets (Phase 3).
//
// The first multi-dataset widget: it reads five betting-market datasets via the
// accessor and hands them to the pure shaper. Emits the SAME DOM + class names
// as the legacy renderValueIndicatorModule (verified by the visual A/B). No
// controls — it keys off the fixture from the bridge context.
//
// The legacy renderer adds `betting-module` to the *placed module* (a dark
// betting theme on the header/controls/body). The React root is the module body,
// so a layout effect tags the enclosing `.placed-module` and removes it on
// unmount — in Phase 4 this becomes a className on the module shell JSX.
//
// M2 wave 12 (the LAST widget cutover): the five market datasets now arrive from
// app-api as ONE `value-indicator` envelope (a `ValueIndicatorSources`), replacing
// the five `getX` getters. The host-tag effect keys on `[isPending]` (not `[]`)
// so it re-tags once `.vi-wrapper` (and thus the ref) mounts on data-arrival —
// otherwise an `[]`-deps effect fires against the skeleton (ref null) and never
// re-tags (the MarginCalculator gotcha, wave 9).

import { useLayoutEffect, useRef } from "react";
import { shapeValueIndicator, edgeText, edgeColor, type ValueIndicatorSources } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface ValueIndicatorProps {
  fixture: string;
}

/** Empty sources so the shaper has a valid shape before the fetch resolves. */
const EMPTY_SOURCES: ValueIndicatorSources = {
  winner: {},
  goals: {},
  cards: {},
  playerGoals: {},
  playerCards: {},
};

export default function ValueIndicator({ fixture }: ValueIndicatorProps) {
  const { data, isPending, isError, refetch } = useWidgetData(
    "value-indicator",
    useFixtureKey(fixture)
  );

  // Tag the enclosing placed module so the shared CSS applies the betting theme.
  const hostRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const mod = hostRef.current?.closest<HTMLElement>(".placed-module");
    if (!mod) return;
    mod.classList.add("betting-module");
    return () => mod.classList.remove("betting-module");
    // `isPending` in the deps so the tag lands once `.vi-wrapper` mounts on load.
  }, [isPending]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const rows = shapeValueIndicator(fixture, data ?? EMPTY_SOURCES);

  return (
    <div className="vi-wrapper" ref={hostRef}>
      <div className="bm-hdr">
        <div className="vi-hdr-mkt bm-hdr-cell">Mkt</div>
        <div className="vi-hdr-out bm-hdr-cell">Outcome</div>
        <div className="vi-hdr-num bm-hdr-cell">Model</div>
        <div className="vi-hdr-num vi-hdr-num--pl bm-hdr-cell">PN</div>
        <div className="vi-hdr-edge bm-hdr-cell">Edge</div>
      </div>
      <div className="bm-scroll">
        {rows.map((c, i) => (
          <div
            className={`vi-row${i % 2 === 1 ? " vi-row--alt" : ""}`}
            key={`${c.market}-${c.outcome}-${i}`}
          >
            <div className="vi-mkt">{c.market}</div>
            <div className="vi-out">{c.outcome}</div>
            <div className="vi-num">{c.model.toFixed(2)}</div>
            <div className="vi-num">{c.pn.toFixed(2)}</div>
            <div className="vi-edge" style={{ color: edgeColor(c.edge) }}>
              {edgeText(c.edge)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
